import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useWallet } from '@solana/wallet-adapter-react';
import { Link } from 'react-router-dom';
import { useSeasonPointsStore, SeasonPointsEntry, PointEvent } from '../../state/seasonPointsStore';
import { shortKey } from '../../utils/format';
import {
  TrophyIcon,
  FireIcon,
  StarIcon,
  ChartBarIcon,
  ClockIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import { TokenRewardsPanel } from '../components/TokenRewardsPanel';
import { SeasonShareCard } from '../components/SeasonShareCard';
import { AnimatePresence } from 'framer-motion';
import { ShareIcon } from '@heroicons/react/24/outline';

function formatDaysRemaining(endDate: string): string {
  const now = new Date();
  const end = new Date(endDate);
  const diff = end.getTime() - now.getTime();
  if (diff <= 0) return 'Ended';
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
  return `${days}d remaining`;
}

function formatMultiplierName(key: string): string {
  const names: Record<string, string> = {
    win: 'WIN',
    streak: 'STREAK',
    rank: 'RANK',
    happyHour: 'HAPPY HOUR',
    season: 'SEASON',
    staking: 'STAKING',
  };
  return names[key] ?? key.toUpperCase();
}

function formatEventType(type: string): string {
  const types: Record<string, string> = {
    match_win: 'Match Win',
    match_loss: 'Match Loss',
    bonus: 'Bonus',
  };
  return types[type] ?? type;
}

function formatTimeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

type Tab = 'leaderboard' | 'history';

export const SeasonsPage: React.FC = () => {
  const { publicKey } = useWallet();
  const wallet = publicKey?.toBase58();
  const [activeTab, setActiveTab] = useState<Tab>('leaderboard');
  const [showShareCard, setShowShareCard] = useState(false);

  const {
    currentSeason,
    myPoints,
    myRank,
    leaderboard,
    pointHistory,
    lifetimePoints,
    loading,
    fetchCurrentSeason,
    fetchMyPoints,
    fetchLeaderboard,
    fetchPointHistory,
    fetchLifetimePoints,
  } = useSeasonPointsStore();

  useEffect(() => {
    fetchCurrentSeason();
  }, [fetchCurrentSeason]);

  useEffect(() => {
    if (currentSeason && wallet) {
      fetchMyPoints(wallet, currentSeason.id);
      fetchLifetimePoints(wallet);
    }
  }, [currentSeason, wallet, fetchMyPoints, fetchLifetimePoints]);

  useEffect(() => {
    if (currentSeason) {
      fetchLeaderboard(currentSeason.id);
    }
  }, [currentSeason, fetchLeaderboard]);

  useEffect(() => {
    if (currentSeason && wallet && activeTab === 'history') {
      fetchPointHistory(wallet, currentSeason.id);
    }
  }, [currentSeason, wallet, activeTab, fetchPointHistory]);

  const winRate =
    myPoints && myPoints.matchesPlayed > 0
      ? Math.round((myPoints.matchesWon / myPoints.matchesPlayed) * 100)
      : 0;

  return (
    <div className="container mx-auto max-w-4xl px-4 py-8">
      {/* Header */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="mb-8 text-center"
      >
        <div className="mb-2 flex items-center justify-center gap-3">
          <h1 className="neon-text font-display text-3xl font-bold">SEASONS</h1>
          {currentSeason && (
            <span className="rounded-full border border-arcade-cyan/50 bg-arcade-cyan/20 px-3 py-1 font-display text-xs text-arcade-cyan">
              {currentSeason.name.toUpperCase()}
            </span>
          )}
        </div>
        <p className="text-white/50">
          Earn points by playing matches. Higher wagers = more points.
          {currentSeason && (
            <span className="ml-2 text-arcade-cyan">
              {formatDaysRemaining(currentSeason.endDate)}
            </span>
          )}
        </p>
      </motion.div>

      {/* Your Stats Card */}
      {wallet && myPoints && (
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="arcade-panel pixel-corners mb-6 rounded-xl border border-arcade-cyan/30 bg-arcade-cyan/5 p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="font-pixel text-xs text-white/60">YOUR RANK</div>
              <div className="neon-text font-display text-2xl">#{myRank || '—'}</div>
            </div>
            <div className="text-right">
              <div className="font-pixel text-xs text-white/60">SEASON POINTS</div>
              <div className="font-display text-2xl text-arcade-cyan">
                {Math.floor(myPoints.points).toLocaleString()}
              </div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-4 font-pixel text-xs">
            <div>
              <div className="text-white/50">Matches</div>
              <div className="text-arcade-yellow">{myPoints.matchesPlayed}</div>
            </div>
            <div>
              <div className="text-white/50">Wins</div>
              <div className="text-arcade-green">{myPoints.matchesWon}</div>
            </div>
            <div>
              <div className="text-white/50">Win Rate</div>
              <div className="text-arcade-pink">{winRate}%</div>
            </div>
            <div>
              <div className="text-white/50">Lifetime</div>
              <div className="text-arcade-purple">
                {Math.floor(lifetimePoints).toLocaleString()}
              </div>
            </div>
          </div>
          <button
            onClick={() => setShowShareCard(true)}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-white/20 bg-white/5 py-2 font-pixel text-xs text-white/60 transition hover:bg-white/10 hover:text-white"
          >
            <ShareIcon className="h-3.5 w-3.5" />
            Share Season Card
          </button>
        </motion.div>
      )}

      {/* Season Share Card Modal */}
      <AnimatePresence>
        {showShareCard && currentSeason && myPoints && (
          <SeasonShareCard
            seasonName={currentSeason.name}
            rank={myRank}
            points={myPoints.points}
            matchesPlayed={myPoints.matchesPlayed}
            matchesWon={myPoints.matchesWon}
            multiplier={currentSeason.pointsMultiplier}
            onClose={() => setShowShareCard(false)}
          />
        )}
      </AnimatePresence>

      {/* No wallet connected */}
      {!wallet && (
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="arcade-panel pixel-corners mb-6 rounded-xl p-6 text-center"
        >
          <p className="font-arcade text-white/60">Connect your wallet to see your stats</p>
        </motion.div>
      )}

      {/* Season Multiplier Badge */}
      {currentSeason && currentSeason.pointsMultiplier > 1 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-6 flex items-center justify-center gap-2 rounded-lg border border-arcade-yellow/30 bg-arcade-yellow/10 px-4 py-2"
        >
          <BoltIcon className="h-4 w-4 text-arcade-yellow" />
          <span className="font-pixel text-xs text-arcade-yellow">
            SEASON MULTIPLIER ACTIVE: {currentSeason.pointsMultiplier}x
          </span>
        </motion.div>
      )}

      {/* Tab Selector */}
      <div className="mb-6 flex justify-center gap-2">
        {[
          { key: 'leaderboard' as Tab, label: 'Leaderboard', icon: ChartBarIcon },
          { key: 'history' as Tab, label: 'Point History', icon: ClockIcon },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`arcade-panel flex items-center gap-2 rounded-lg px-4 py-2 font-pixel text-sm transition-all ${
              activeTab === tab.key
                ? 'border-arcade-cyan bg-arcade-cyan/20 text-arcade-cyan'
                : 'border-white/20 text-white/60 hover:border-white/40 hover:text-white'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Leaderboard Tab */}
      {activeTab === 'leaderboard' && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="arcade-panel pixel-corners overflow-hidden rounded-xl"
        >
          {/* Table Header */}
          <div className="grid grid-cols-10 gap-2 border-b border-white/20 bg-black/30 px-2 py-3 font-pixel text-xs text-white/60 md:grid-cols-12 md:px-4">
            <div className="col-span-1 text-center">#</div>
            <div className="col-span-4">Player</div>
            <div className="col-span-2 text-center md:col-span-3">Points</div>
            <div className="col-span-3 text-center md:col-span-2">W/L</div>
            <div className="hidden text-center md:block md:col-span-2">Matches</div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="space-y-1">
              {Array.from({ length: 10 }).map((_, i) => (
                <div
                  key={i}
                  className="grid grid-cols-10 gap-2 border-b border-white/5 px-2 py-3 md:grid-cols-12 md:px-4"
                >
                  <div className="col-span-1">
                    <div className="mx-auto h-4 w-6 animate-pulse rounded bg-white/10" />
                  </div>
                  <div className="col-span-4">
                    <div className="h-4 w-24 animate-pulse rounded bg-white/10" />
                  </div>
                  <div className="col-span-2 md:col-span-3">
                    <div className="mx-auto h-4 w-16 animate-pulse rounded bg-white/10" />
                  </div>
                  <div className="col-span-3 md:col-span-2">
                    <div className="mx-auto h-4 w-12 animate-pulse rounded bg-white/10" />
                  </div>
                  <div className="hidden md:block md:col-span-2">
                    <div className="mx-auto h-4 w-10 animate-pulse rounded bg-white/10" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && leaderboard.length === 0 && (
            <div className="py-12 text-center">
              <TrophyIcon className="mx-auto mb-3 h-10 w-10 text-white/30" />
              <div className="font-arcade text-white/60">No players on the leaderboard yet</div>
              <Link
                to="/matchmaking"
                className="mt-4 inline-block rounded-lg bg-arcade-cyan/20 px-4 py-2 font-pixel text-sm text-arcade-cyan transition-all hover:bg-arcade-cyan/30"
              >
                Be the first to play!
              </Link>
            </div>
          )}

          {/* Entries */}
          {!loading &&
            leaderboard.map((entry: SeasonPointsEntry) => {
              const isCurrentPlayer = wallet && entry.wallet === wallet;
              const isTopThree = entry.rank <= 3;
              const losses = entry.matchesPlayed - entry.matchesWon;

              return (
                <div
                  key={entry.wallet}
                  className={`grid grid-cols-10 items-center gap-2 border-b border-white/10 px-2 py-3 transition-colors md:grid-cols-12 md:px-4 ${
                    isCurrentPlayer
                      ? 'bg-arcade-cyan/10'
                      : isTopThree
                        ? 'bg-white/5'
                        : 'hover:bg-white/5'
                  }`}
                >
                  {/* Rank */}
                  <div className="col-span-1 text-center">
                    {entry.rank <= 3 ? (
                      <span
                        className={`font-display text-sm font-bold ${
                          entry.rank === 1
                            ? 'text-yellow-400'
                            : entry.rank === 2
                              ? 'text-slate-300'
                              : 'text-amber-600'
                        }`}
                      >
                        {entry.rank}
                      </span>
                    ) : (
                      <span className="font-pixel text-white/60">{entry.rank}</span>
                    )}
                  </div>

                  {/* Player */}
                  <div className="col-span-4">
                    <div
                      className={`font-arcade text-sm ${isCurrentPlayer ? 'text-arcade-cyan' : 'text-white'}`}
                    >
                      {isCurrentPlayer ? 'You' : shortKey(entry.wallet)}
                    </div>
                  </div>

                  {/* Points */}
                  <div className="col-span-2 text-center md:col-span-3">
                    <span className="neon-text font-display text-lg">
                      {Math.floor(entry.points).toLocaleString()}
                    </span>
                  </div>

                  {/* Win/Loss */}
                  <div className="col-span-3 text-center font-pixel text-xs md:col-span-2">
                    <span className="text-arcade-green">{entry.matchesWon}</span>
                    <span className="text-white/30">/</span>
                    <span className="text-arcade-pink">{losses}</span>
                  </div>

                  {/* Matches */}
                  <div className="hidden text-center font-pixel text-xs text-white/50 md:block md:col-span-2">
                    {entry.matchesPlayed}
                  </div>
                </div>
              );
            })}
        </motion.div>
      )}

      {/* Point History Tab */}
      {activeTab === 'history' && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="space-y-3"
        >
          {!wallet && (
            <div className="arcade-panel pixel-corners rounded-xl p-6 text-center">
              <p className="font-arcade text-white/60">Connect your wallet to view point history</p>
            </div>
          )}

          {wallet && pointHistory.length === 0 && (
            <div className="arcade-panel pixel-corners rounded-xl py-12 text-center">
              <StarIcon className="mx-auto mb-3 h-10 w-10 text-white/30" />
              <div className="font-arcade text-white/60">No points earned yet this season</div>
              <Link
                to="/matchmaking"
                className="mt-4 inline-block rounded-lg bg-arcade-cyan/20 px-4 py-2 font-pixel text-sm text-arcade-cyan transition-all hover:bg-arcade-cyan/30"
              >
                Play a match to earn points!
              </Link>
            </div>
          )}

          {wallet &&
            pointHistory.map((event: PointEvent) => {
              const isWin = event.eventType === 'match_win';
              const activeMultipliers = Object.entries(event.multipliers).filter(([, v]) => v > 1);

              return (
                <div key={event.id} className="arcade-panel rounded-lg border-white/10 p-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-3">
                      <div
                        className={`flex h-8 w-8 items-center justify-center rounded-lg ${
                          isWin ? 'bg-arcade-green/20' : 'bg-white/10'
                        }`}
                      >
                        {isWin ? (
                          <TrophyIcon className="h-4 w-4 text-arcade-green" />
                        ) : (
                          <FireIcon className="h-4 w-4 text-white/50" />
                        )}
                      </div>
                      <div>
                        <div className="font-arcade text-sm text-white">
                          {formatEventType(event.eventType)}
                        </div>
                        <div className="font-pixel text-xs text-white/40">
                          {formatTimeAgo(event.createdAt)}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div
                        className={`font-display text-lg font-bold ${isWin ? 'text-arcade-green' : 'text-white/70'}`}
                      >
                        +{Math.floor(event.finalPoints).toLocaleString()}
                      </div>
                      <div className="font-pixel text-xs text-white/40">
                        base: {Math.floor(event.basePoints)}
                      </div>
                    </div>
                  </div>

                  {/* Multiplier Badges */}
                  {activeMultipliers.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {activeMultipliers.map(([key, value]) => (
                        <span
                          key={key}
                          className="rounded-full border border-arcade-yellow/30 bg-arcade-yellow/10 px-2 py-0.5 font-pixel text-[10px] text-arcade-yellow"
                        >
                          {formatMultiplierName(key)} {value}x
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
        </motion.div>
      )}

      {/* Token Rewards */}
      {wallet && (
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="mt-6">
          <TokenRewardsPanel currentPoints={myPoints?.points ?? 0} />
        </motion.div>
      )}

      {/* Multiplier Info */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="mt-8"
      >
        <h2 className="mb-4 text-center font-display text-lg text-white/70">
          How to Earn More Points
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[
            {
              icon: TrophyIcon,
              title: 'Win Matches',
              desc: 'Winners earn 2x points',
              color: 'text-arcade-green',
              bg: 'bg-arcade-green/10',
            },
            {
              icon: FireIcon,
              title: 'Win Streaks',
              desc: '3-win: 1.5x, 5-win: 2x, 10-win: 3x',
              color: 'text-arcade-pink',
              bg: 'bg-arcade-pink/10',
            },
            {
              icon: ChartBarIcon,
              title: 'Climb Ranks',
              desc: 'Higher rank = higher multiplier',
              color: 'text-arcade-cyan',
              bg: 'bg-arcade-cyan/10',
            },
            {
              icon: BoltIcon,
              title: 'Happy Hours',
              desc: 'Daily 2x, weekend 1.5x bonus',
              color: 'text-arcade-yellow',
              bg: 'bg-arcade-yellow/10',
            },
            {
              icon: StarIcon,
              title: 'Bigger Wagers',
              desc: 'More SOL = more base points',
              color: 'text-arcade-purple',
              bg: 'bg-arcade-purple/10',
            },
            {
              icon: ChartBarIcon,
              title: 'Stake Tokens',
              desc: 'Stakers earn up to 2.5x bonus',
              color: 'text-joker',
              bg: 'bg-joker/10',
            },
          ].map((item) => (
            <div key={item.title} className="arcade-panel flex items-start gap-3 rounded-lg p-3">
              <div
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${item.bg}`}
              >
                <item.icon className={`h-4 w-4 ${item.color}`} />
              </div>
              <div>
                <div className={`font-pixel text-xs font-bold ${item.color}`}>{item.title}</div>
                <div className="font-pixel text-[10px] text-white/50">{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Back to Home */}
      <div className="mt-8 text-center">
        <Link
          to="/"
          className="font-pixel text-sm text-white/60 transition-colors hover:text-white"
        >
          Back to Home
        </Link>
      </div>
    </div>
  );
};
